import { CANVAS_CONFIG, COLOR_PALETTE, type PaletteColor } from '@the-wall/shared';

export interface OverlayBounds {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

/** Squared distance between two hex colors in RGB space (no sqrt for comparison). */
function colorDistanceSq(hex1: string, hex2: string): number {
  const r1 = parseInt(hex1.slice(1, 3), 16);
  const g1 = parseInt(hex1.slice(3, 5), 16);
  const b1 = parseInt(hex1.slice(5, 7), 16);
  const r2 = parseInt(hex2.slice(1, 3), 16);
  const g2 = parseInt(hex2.slice(3, 5), 16);
  const b2 = parseInt(hex2.slice(5, 7), 16);
  return (r1 - r2) ** 2 + (g1 - g2) ** 2 + (b1 - b2) ** 2;
}

/** Find the palette color closest to the given hex. */
function nearestPaletteColor(hex: string): PaletteColor {
  let best: PaletteColor = (COLOR_PALETTE[0] ?? '#FFFFFF') as PaletteColor;
  let bestDist = Infinity;
  for (const p of COLOR_PALETTE) {
    const d = colorDistanceSq(hex, p);
    if (d < bestDist) {
      bestDist = d;
      best = p;
    }
  }
  return best;
}

/** Convert rgba to #rrggbb (ignores alpha). */
function rgbaToHex(r: number, g: number, b: number): string {
  return '#' + [r, g, b].map((c) => Math.round(c).toString(16).padStart(2, '0')).join('');
}

/** Treat pixel as "empty" (transparent in overlay) if very light or very transparent. */
function isEmptyPixel(data: Uint8ClampedArray, i: number, alphaThreshold: number): boolean {
  const a = data[i + 3] ?? 0;
  if (a < alphaThreshold) return true;
  const r = data[i] ?? 0;
  const g = data[i + 1] ?? 0;
  const b = data[i + 2] ?? 0;
  const luminance = (r * 299 + g * 587 + b * 114) / 1000;
  return luminance >= 250; // treat near-white as empty
}

const DEFAULT_ALPHA_THRESHOLD = 80;

/** Clamp bounds to canvas and ensure x1 <= x2, y1 <= y2. */
function clampBounds(
  bounds: OverlayBounds,
  canvasWidth: number,
  canvasHeight: number
): OverlayBounds {
  const maxX = canvasWidth - 1;
  const maxY = canvasHeight - 1;
  return {
    x1: Math.max(0, Math.min(maxX, Math.floor(bounds.x1))),
    y1: Math.max(0, Math.min(maxY, Math.floor(bounds.y1))),
    x2: Math.max(0, Math.min(maxX, Math.floor(bounds.x2))),
    y2: Math.max(0, Math.min(maxY, Math.floor(bounds.y2))),
  };
}

export interface ImageToOverlayResult {
  grid: (PaletteColor | null)[][];
  bounds: OverlayBounds;
}

/**
 * Load an image file, scale to the given canvas region, sample pixels and snap to palette.
 * Returns grid with dimensions (y2-y1+1) x (x2-x1+1) and the clamped bounds used.
 */
export function imageToOverlayPixels(
  file: File,
  options?: {
    alphaThreshold?: number;
    bounds?: OverlayBounds;
    canvasWidth?: number;
    canvasHeight?: number;
  }
): Promise<ImageToOverlayResult> {
  const alphaThreshold = options?.alphaThreshold ?? DEFAULT_ALPHA_THRESHOLD;
  const canvasWidth = options?.canvasWidth ?? CANVAS_CONFIG.WIDTH;
  const canvasHeight = options?.canvasHeight ?? CANVAS_CONFIG.HEIGHT;
  const rawBounds = options?.bounds ?? {
    x1: 0,
    y1: 0,
    x2: canvasWidth - 1,
    y2: canvasHeight - 1,
  };
  const bounds = clampBounds(rawBounds, canvasWidth, canvasHeight);
  const { x1, y1, x2, y2 } = bounds;
  const w = Math.max(1, x2 - x1 + 1);
  const h = Math.max(1, y2 - y1 + 1);

  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          reject(new Error('Could not get canvas context'));
          return;
        }
        ctx.drawImage(img, 0, 0, w, h);
        const imageData = ctx.getImageData(0, 0, w, h);
        const data = imageData.data;

        const grid: (PaletteColor | null)[][] = Array.from({ length: h }, () =>
          Array.from({ length: w }, () => null)
        );

        for (let y = 0; y < h; y++) {
          for (let x = 0; x < w; x++) {
            const i = (y * w + x) * 4;
            if (isEmptyPixel(data, i, alphaThreshold)) continue;
            const r = data[i] ?? 0;
            const g = data[i + 1] ?? 0;
            const b = data[i + 2] ?? 0;
            const hex = rgbaToHex(r, g, b);
            const row = grid[y];
            if (row) row[x] = nearestPaletteColor(hex);
          }
        }

        resolve({ grid, bounds });
      } finally {
        URL.revokeObjectURL(url);
      }
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };

    img.src = url;
  });
}
